import React from 'react';
import { Link } from 'react-router-dom';
import { useDashboardStore } from '../stores/dashboardStore';
import ProjectCarousel from '../components/ProjectCarousel';
import InternshipCardSimple from '../components/InternshipCardSimple';
import './Home.css';

const Home: React.FC = () => {
    const { config } = useDashboardStore();

    return (
        <div className="home">
            {/* Hero Section */}
            <section className="hero">
                <div className="hero-container">
                    <div className="hero-content">
                        <h1 className="hero-title">
                            你好，我是 {config.personalInfo.name}
                        </h1>
                        <p className="hero-subtitle">{config.personalInfo.title}</p>
                        <p className="hero-description">{config.personalInfo.description}</p>
                        <div className="hero-actions">
                            <Link to="/resume" className="btn btn-primary">
                                下载简历
                            </Link>
                            <a href="#projects" className="btn btn-secondary">
                                浏览项目
                            </a>
                        </div>
                    </div>
                    <div className="hero-image">
                        <img src={config.personalInfo.avatar} alt={config.personalInfo.name} />
                    </div>
                </div>
            </section>

            {/* Education Section */}
            <section className="education-section">
                <div className="container">
                    <h2 className="section-title">{config.education.title}</h2>
                    <p className="section-description">{config.education.description}</p>

                    <div className="education-grid">
                        {config.education.experiences.map((exp, index) => (
                            <div key={index} className="education-card">
                                <div className="education-header">
                                    <h3>{exp.school}</h3>
                                    <span className="period">{exp.period}</span>
                                </div>
                                <div className="education-details">
                                    <p className="degree">{exp.degree} - {exp.major}</p>
                                    <div className="stats">
                                        {exp.gpa && <span className="stat-item">GPA: {exp.gpa}</span>}
                                        {exp.ranking && <span className="stat-item">排名: {exp.ranking}</span>}
                                    </div>
                                    {exp.honors && exp.honors.length > 0 && (
                                        <div className="honors">
                                            <strong>主要荣誉：</strong>
                                            <ul>
                                                {exp.honors.map((honor, honorIndex) => (
                                                    <li key={honorIndex}>{honor}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {exp.papers && exp.papers.length > 0 && (
                                        <div className="papers">
                                            <strong>学术论文：</strong>
                                            <ul>
                                                {exp.papers.map((paper, paperIndex) => (
                                                    <li key={paperIndex}>{paper}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {exp.description && <p className="description">{exp.description}</p>}
                                    {exp.learningPoints && exp.learningPoints.length > 0 && (
                                        <div className="learning-points">
                                            <strong>学习要点：</strong>
                                            <ul>
                                                {exp.learningPoints.map((point, pointIndex) => (
                                                    <li key={pointIndex}>{point}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {exp.relevance && (
                                        <div className="relevance">
                                            <strong>相关课程：</strong>
                                            <p>{exp.relevance}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {config.education.courses && config.education.courses.length > 0 && (
                        <div className="courses-section">
                            <h3>相关课程</h3>
                            <div className="courses-grid">
                                {config.education.courses.map((course, index) => (
                                    <div key={index} className="course-card">
                                        <h4>{course.name}</h4>
                                        <p>{course.description}</p>
                                        <div className="course-relevance">
                                            <strong>相关性：</strong>
                                            <span>{course.relevance}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Projects Section */}
            <section id="projects" className="projects-section">
                <div className="container">
                    <h2 className="section-title">个人项目</h2>
                    <p className="section-description">
                        以下项目展示了我的技术能力和产品思维，每个项目都体现了与产品经理角色的高度匹配。
                    </p>
                    <ProjectCarousel />
                </div>
            </section>

            {/* Internships Section */}
            <section className="internships-section">
                <div className="container">
                    <h2 className="section-title">实习经历</h2>
                    <p className="section-description">
                        在知名公司的实习经历让我深入理解了产品经理的工作内容和核心技能。
                    </p>
                    <div className="internships-grid-simple">
                        {config.internships.map((internship) => (
                            <InternshipCardSimple key={internship.id} internship={internship} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Book Reviews Section */}
            <section className="book-reviews-section">
                <div className="container">
                    <h2 className="section-title">读书心得</h2>
                    <p className="section-description">
                        通过阅读相关书籍，我不断学习和提升产品管理能力。
                    </p>
                    <div className="books-grid">
                        {config.bookReviews.map((book, index) => (
                            <div key={index} className="book-card">
                                <div className="book-cover">
                                    <img src={book.cover} alt={book.title} />
                                    <div className="book-rating">
                                        {'★'.repeat(book.rating)}{'☆'.repeat(5 - book.rating)}
                                    </div>
                                </div>
                                <div className="book-info">
                                    <h3>{book.title}</h3>
                                    <p className="author">作者：{book.author}</p>
                                    <p className="review">{book.review}</p>
                                    <div className="key-insights">
                                        <h4>关键洞察：</h4>
                                        <ul>
                                            {book.keyInsights.map((insight, insightIndex) => (
                                                <li key={insightIndex}>{insight}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
